import React, { useMemo, useState } from 'react';
import {
  PERF_HUB_INTENT_TAGS,
  PERF_HUB_SHORTLIST_BASE,
  PERF_HUB_SHORTLIST_INTENTS,
  collectCrmCampaignFilterOptions,
  crmEntryCustomTags,
  crmEntryIntentTags,
  crmEntryShortlisted,
  crmFormStateFromEntry,
  EMPTY_CRM_FORM,
  extractCrmCampaignNames,
  filterShortlistedCrm,
  formatCrmTagLabel,
  formatShortlistIntentLabels,
  getCrmEntryForHandle,
  partitionCrmEntries,
  primaryCrmCampaignName,
} from './perfHubShortlistTags';
import { downloadCrmShortlistCsv } from './perfHubCrmExport';

const CRM_INTENT_FILTERS = [
  { id: 'all', label: 'All intents' },
  { id: 'rebook', label: 'Re-book' },
  { id: 'phase-2', label: 'Phase 2' },
  { id: 'ugc', label: 'UGC' },
];

function normCrmHandle(h) {
  return (h || '').trim().replace(/^@/, '').toLowerCase();
}

function CrmShortlistTable({ rows, busy, onRemove, showCampaign = true }) {
  if (!rows.length) return null;
  return (
    <div className="ph-table-wrap ph-crm-table-wrap">
      <table className="funds-table enhanced ph-crm-table">
        <thead>
          <tr>
            <th>Handle</th>
            {showCampaign && <th>Campaign</th>}
            <th>{showCampaign ? 'Intents' : 'Tags'}</th>
            <th>Email</th>
            <th>Notes</th>
            <th className="ph-crm-th-actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const intents = crmEntryIntentTags(c);
            const customTags = crmEntryCustomTags(c);
            const tagPills = showCampaign
              ? intents.map((t) => ({ key: t, label: formatCrmTagLabel(t), intent: true }))
              : [...intents, ...customTags].map((t) => ({
                  key: t,
                  label: formatCrmTagLabel(t) || t,
                  intent: PERF_HUB_INTENT_TAGS.includes(t),
                }));
            const campaigns = extractCrmCampaignNames(c);
            const campaignLabel =
              campaigns.length > 1
                ? `${primaryCrmCampaignName(c) || campaigns[0]} (+${campaigns.length - 1})`
                : primaryCrmCampaignName(c) || campaigns[0] || '—';
            return (
              <tr key={c.id}>
                <td className="ph-crm-td-handle">
                  <strong>@{c.instagram_handle}</strong>
                </td>
                {showCampaign && (
                  <td className="ph-crm-td-campaign" title={campaigns.join(', ') || undefined}>
                    {campaignLabel}
                  </td>
                )}
                <td className="ph-crm-td-tags">
                  {tagPills.length > 0 ? (
                    <div className="ph-crm-tag-row">
                      {tagPills.map((pill) => (
                        <span
                          key={`${c.id}-${pill.key}`}
                          className={
                            pill.intent
                              ? 'ph-crm-tag-pill ph-crm-tag-pill--intent'
                              : 'ph-crm-tag-pill'
                          }
                        >
                          {pill.label}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="ph-cell-muted">{showCampaign ? 'Saved' : '—'}</span>
                  )}
                </td>
                <td className="ph-crm-td-email">
                  {c.email ? (
                    <a className="ph-crm-mailto" href={`mailto:${c.email}`}>
                      {c.email}
                    </a>
                  ) : (
                    <span className="ph-cell-muted">—</span>
                  )}
                </td>
                <td className="ph-crm-td-notes">
                  {c.notes ? (
                    <span className="ph-crm-notes-preview" title={c.notes}>
                      {c.notes}
                    </span>
                  ) : (
                    <span className="ph-cell-muted">—</span>
                  )}
                </td>
                <td className="ph-crm-td-actions">
                  <button
                    type="button"
                    className="btn-outline ph-btn-crm-remove"
                    disabled={busy}
                    onClick={() => onRemove(c.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


/**
 * Performance Hub CRM tab — shortlisted creators (filtered) first, manual add below.
 */
export default function PerfHubCrmTab({
  crm,
  busy,
  crmForm,
  setCrmForm,
  campaigns = [],
  onSave,
  onRemove,
  exportEnabled = true,
}) {
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [intentFilter, setIntentFilter] = useState('all');

  const { shortlisted, other } = useMemo(() => partitionCrmEntries(crm), [crm]);
  const campaignOptions = useMemo(() => collectCrmCampaignFilterOptions(crm), [crm]);
  const filteredShortlist = useMemo(
    () => filterShortlistedCrm(crm, { campaign: campaignFilter, intent: intentFilter }),
    [crm, campaignFilter, intentFilter]
  );

  const hasShortlist = shortlisted.length > 0;
  const filtersActive = campaignFilter !== 'all' || intentFilter !== 'all';
  const canDownload = exportEnabled && filteredShortlist.length > 0;

  const handleDownload = () => {
    if (!canDownload) return;
    downloadCrmShortlistCsv(filteredShortlist, { campaignFilter, intentFilter });
  };

  const editingEntry = useMemo(() => {
    const h = normCrmHandle(crmForm.instagram_handle);
    return h ? getCrmEntryForHandle(crm, h) : null;
  }, [crm, crmForm.instagram_handle]);

  const toggleFormIntent = (intentId) => {
    setCrmForm((prev) => {
      const intents = Array.isArray(prev.intents) ? [...prev.intents] : [];
      const idx = intents.indexOf(intentId);
      if (idx >= 0) {
        intents.splice(idx, 1);
      } else {
        intents.push(intentId);
      }
      return { ...prev, intents };
    });
  };

  const handleHandleBlur = () => {
    const h = normCrmHandle(crmForm.instagram_handle);
    if (!h) return;
    const existing = getCrmEntryForHandle(crm, h);
    if (!existing) return;
    setCrmForm((prev) => {
      const loaded = crmFormStateFromEntry(existing);
      return {
        ...loaded,
        instagram_handle: prev.instagram_handle || loaded.instagram_handle,
      };
    });
  };

  const formIntents = Array.isArray(crmForm.intents) ? crmForm.intents : [];

  return (
    <>
      <section className="section-card ph-glass-card ph-section-card ph-crm-list-section">
        <div className="ph-crm-section-head">
          <h3>
            <span className="ph-section-icon">⭐</span>
            Your shortlist
            {shortlisted.length > 0 && (
              <span className="ph-crm-count-pill" aria-label={`${shortlisted.length} shortlisted`}>
                {shortlisted.length}
              </span>
            )}
          </h3>
          {hasShortlist && (
            <button
              type="button"
              className="btn-outline ph-crm-download-btn"
              disabled={!canDownload}
              onClick={handleDownload}
              title={
                !exportEnabled
                  ? 'CSV export requires a paid plan'
                  : filteredShortlist.length === 0
                    ? 'No rows match these filters'
                    : 'Download shortlisted creators for the current filters'
              }
            >
              Download CSV {!exportEnabled ? '(paid)' : ''}
            </button>
          )}
        </div>
        <p className="ph-crm-section-lead">
          Creators saved from Overview (Creator Intelligence or Top performers). Filter by campaign,
          then by re-book, phase 2, or UGC intent.
        </p>

        {!hasShortlist && (
          <div className="ph-crm-empty">
            <span className="ph-crm-empty-icon" aria-hidden>
              🗂️
            </span>
            <h4 className="ph-crm-empty-title">No shortlisted creators yet</h4>
            <p className="ph-crm-empty-copy">
              Open a campaign under <strong>Overview</strong> and use <strong>Add to shortlist</strong>{' '}
              on a creator row, or add a handle manually in the section below.
            </p>
          </div>
        )}

        {hasShortlist && (
          <>
            <div className="ph-crm-filters">
              <div className="ph-crm-filter-group">
                <label className="ph-crm-filter-label" htmlFor="ph-crm-campaign-filter">
                  Campaign
                </label>
                <select
                  id="ph-crm-campaign-filter"
                  className="ph-crm-filter-select"
                  value={campaignFilter}
                  onChange={(e) => setCampaignFilter(e.target.value)}
                >
                  <option value="all">All campaigns</option>
                  {campaignOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ph-crm-filter-group ph-crm-filter-group--intents">
                <span className="ph-crm-filter-label">Intent</span>
                <div className="ph-crm-intent-filters" role="tablist" aria-label="Shortlist intent">
                  {CRM_INTENT_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      role="tab"
                      aria-selected={intentFilter === f.id}
                      className={`ph-crm-intent-filter ${intentFilter === f.id ? 'active' : ''}`}
                      onClick={() => setIntentFilter(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filtersActive && (
              <p className="ph-crm-filter-summary ph-cell-muted">
                Showing {filteredShortlist.length} of {shortlisted.length}
                {campaignFilter !== 'all' ? ` · ${campaignFilter}` : ''}
                {intentFilter !== 'all'
                  ? ` · ${formatShortlistIntentLabels([intentFilter])}`
                  : ''}
              </p>
            )}

            {filteredShortlist.length > 0 ? (
              <CrmShortlistTable rows={filteredShortlist} busy={busy} onRemove={onRemove} />
            ) : (
              <p className="ph-crm-empty-filter ph-cell-muted">No creators match these filters.</p>
            )}
          </>
        )}

        {other.length > 0 && (
          <div className="ph-crm-other-block">
            <h4 className="ph-crm-other-title">Other saved contacts</h4>
            <p className="ph-crm-other-lead ph-cell-muted">
              Handles added manually without shortlist intents ({other.length}).
            </p>
            <CrmShortlistTable rows={other} busy={busy} onRemove={onRemove} showCampaign={false} />
          </div>
        )}
      </section>

      <section className="section-card ph-glass-card ph-section-card ph-crm-add-section">
        <h3>
          <span className="ph-section-icon">➕</span>
          {editingEntry ? 'Update creator' : 'Add creator manually'}
        </h3>
        <p className="ph-crm-section-lead">
          Add or update a creator by Instagram handle. Select shortlist intents so they appear in
          <strong> Your shortlist</strong> above; leave intents empty for a general contact only.
        </p>
        {editingEntry && (
          <p className="ph-crm-editing-banner" role="status">
            Editing existing contact
            {crmEntryShortlisted(editingEntry) ? ' (on your shortlist)' : ''}. Saving updates email,
            notes, intents, and tags without removing prior shortlist data unless you change
            intents.
          </p>
        )}
        <form className="ph-crm-form" onSubmit={onSave}>
          <div className="ph-crm-form-grid">
            <div className="ph-crm-field">
              <label className="ph-field-label" htmlFor="ph-crm-handle">
                Instagram handle
              </label>
              <input
                id="ph-crm-handle"
                className="ph-input ph-input-full"
                placeholder="@creator or creator"
                value={crmForm.instagram_handle}
                onChange={(e) => setCrmForm({ ...crmForm, instagram_handle: e.target.value })}
                onBlur={handleHandleBlur}
                required
                autoComplete="off"
              />
            </div>
            <div className="ph-crm-field">
              <label className="ph-field-label" htmlFor="ph-crm-campaign">
                Linked campaign <span className="ph-optional-label">optional</span>
              </label>
              <select
                id="ph-crm-campaign"
                className="ph-select ph-input-full"
                value={crmForm.source_campaign_name || ''}
                onChange={(e) =>
                  setCrmForm({ ...crmForm, source_campaign_name: e.target.value })
                }
                disabled={busy}
              >
                <option value="">None / not from a campaign</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ph-crm-field">
              <label className="ph-field-label" htmlFor="ph-crm-email">
                Email <span className="ph-optional-label">optional</span>
              </label>
              <input
                id="ph-crm-email"
                type="email"
                className="ph-input ph-input-full"
                placeholder="creator@email.com"
                value={crmForm.email}
                onChange={(e) => setCrmForm({ ...crmForm, email: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="ph-crm-field ph-crm-field-span">
              <fieldset className="ph-crm-intents-fieldset">
                <legend className="ph-field-label ph-crm-intents-legend">
                  Shortlist intents{' '}
                  <span className="ph-optional-label">
                    optional — pick one or more to list under Your shortlist
                  </span>
                </legend>
                <div className="ph-crm-intents-grid">
                  {PERF_HUB_SHORTLIST_INTENTS.map((opt) => {
                    const checked = formIntents.includes(opt.id);
                    return (
                      <label
                        key={opt.id}
                        className={`ph-shortlist-intent ph-crm-intent-option ${checked ? 'active' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="ph-shortlist-intent-check"
                          checked={checked}
                          disabled={busy}
                          onChange={() => toggleFormIntent(opt.id)}
                        />
                        <span className="ph-shortlist-intent-body">
                          <span className="ph-shortlist-intent-label">{opt.label}</span>
                          <span className="ph-shortlist-intent-desc">{opt.description}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </div>
            <div className="ph-crm-field ph-crm-field-span">
              <label className="ph-field-label" htmlFor="ph-crm-tags">
                Other tags <span className="ph-optional-label">optional</span>
              </label>
              <input
                id="ph-crm-tags"
                className="ph-input ph-input-full"
                placeholder="Comma-separated — e.g. priority, skincare, festival"
                value={crmForm.tags}
                onChange={(e) => setCrmForm({ ...crmForm, tags: e.target.value })}
                autoComplete="off"
              />
              <p className="ph-pg-field-hint">
                Custom labels only — re-book, phase 2, and UGC are set with the intents above.
              </p>
            </div>
            <div className="ph-crm-field ph-crm-field-span">
              <label className="ph-field-label" htmlFor="ph-crm-notes">
                Notes <span className="ph-optional-label">optional</span>
              </label>
              <textarea
                id="ph-crm-notes"
                className="ph-input ph-crm-textarea"
                placeholder="Context, last outreach, campaign fit…"
                rows={4}
                value={crmForm.notes}
                onChange={(e) => setCrmForm({ ...crmForm, notes: e.target.value })}
              />
            </div>
          </div>
          <div className="ph-crm-form-actions">
            <button type="submit" className="btn-primary ph-btn-glow" disabled={busy}>
              {busy ? 'Saving…' : editingEntry ? 'Update contact' : 'Save contact'}
            </button>
            {editingEntry && (
              <button
                type="button"
                className="btn-outline"
                disabled={busy}
                onClick={() => setCrmForm({ ...EMPTY_CRM_FORM })}
              >
                Clear form
              </button>
            )}
            <p className="ph-crm-form-hint ph-cell-muted">
              Tip: tab out of the handle field after typing to load an existing contact. From
              Overview, <strong>Add to shortlist</strong> still adds campaign metrics to notes
              automatically.
            </p>
          </div>
        </form>
      </section>
    </>
  );
}
